import {
  type DocumentMeta,
  SelectionManagerProvider,
  useSelectionManager,
} from "@prismicio/editor-fields"
import { pluralize } from "@prismicio/editor-support/String"
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  ErrorBoundary,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  Tooltip,
  useToast,
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"
import { Link } from "react-router-dom"

import { useHasPublisherPermission } from "../DocumentList.utils"
import { type BrokenLink, getBrokenLinks } from "./bulkActionsService"
import { useBulkActionsButton } from "./useBulkActionsButton"
import { transformDocumentToBulkTaskItem } from "./utils"

interface PublishDocumentsButtonProps {
  /**
   * Provided when publishing documents in a release
   */
  releaseId?: string
}

export function PublishDocumentsButton(props: PublishDocumentsButtonProps) {
  const { releaseId } = props

  return (
    <ErrorBoundary renderError={Fallback}>
      <Suspense fallback={<Fallback />}>
        <PublishButton releaseId={releaseId} />
      </Suspense>
    </ErrorBoundary>
  )
}

function PublishButton(props: PublishDocumentsButtonProps) {
  const { releaseId } = props
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [brokenLinks, setBrokenLinks] = useState<BrokenLink[]>([])
  const { showToast } = useToast()

  const { action, disabled, tooltip, getSelectedDocuments } =
    useBulkActionsButton({
      hasPermission: useHasPublisherPermission(),
      releaseId,
    })

  /**
   * Validate if there are any broken links before publishing a document
   */
  function onPublish() {
    const items = getSelectedDocuments().map((document) =>
      transformDocumentToBulkTaskItem(document, releaseId),
    )

    // planned documents can not be handled by the list broken links endpoint
    // because of this we immediately start the publishing of the selected items
    const containsPlannedDocuments = items.some(
      (item) => item.status === "release",
    )
    if (containsPlannedDocuments) {
      action("publish", { items })
      return
    }

    setLoading(true)

    getBrokenLinks({
      action: "publish",
      payload: { items },
    })
      .then((response) => {
        if (response.length === 0) {
          action("publish", { items })
          return
        }

        setBrokenLinks(response)
        setOpen(true)
      })
      .catch((error) => {
        console.warn(error)
        showToast({
          icon: "alert",
          title:
            "We aren't able to validate broken links in the document(s), please try again",
        })
      })
      .finally(() => setLoading(false))
  }

  return (
    <>
      <Tooltip content={tooltip || "Publish"}>
        <IconButton
          icon="upload"
          onClick={onPublish}
          disabled={disabled}
          color="grey"
          loading={loading}
        />
      </Tooltip>
      <SelectionManagerProvider>
        <BrokenLinksDialog
          open={open}
          onOpenChange={setOpen}
          brokenLinks={brokenLinks}
          releaseId={releaseId}
          getSelectedDocuments={getSelectedDocuments}
        />
      </SelectionManagerProvider>
    </>
  )
}

interface BrokenLinksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  brokenLinks: BrokenLink[]
  releaseId: string | undefined
  getSelectedDocuments: () => DocumentMeta[]
}

function BrokenLinksDialog(props: BrokenLinksDialogProps) {
  const { open, onOpenChange, brokenLinks, releaseId, getSelectedDocuments } =
    props
  const { action } = useBulkActionsButton({
    hasPermission: useHasPublisherPermission(),
    releaseId,
  })
  const selectionManager = useSelectionManager()

  const originalDocumentListSelectedItems = getSelectedDocuments().map(
    (document) => transformDocumentToBulkTaskItem(document, releaseId),
  )
  const additionalDocumentsSelectedBasedOnBrokenLinks = brokenLinks
    .filter((brokenLink) => selectionManager.isSelected(brokenLink.documentId))
    .map((document) => transformDocumentToBulkTaskItem(document, releaseId))

  const itemsToPublish = [
    ...originalDocumentListSelectedItems,
    ...additionalDocumentsSelectedBasedOnBrokenLinks,
  ]

  function onProceedWithBrokenLinks() {
    action("publish", { items: itemsToPublish })
    onOpenChange(false)
    selectionManager.clearSelection()
  }

  function onSelectAll() {
    selectionManager.selectedCount === brokenLinks.length
      ? selectionManager.clearSelection()
      : brokenLinks.forEach(
          (brokenLink) =>
            // if some are selected only toggle the none selected ones
            !selectionManager.isSelected(brokenLink.documentId) &&
            selectionManager.toggleSelection({
              id: brokenLink.documentId,
            }),
        )
  }

  function onSelect(brokenLink: BrokenLink) {
    selectionManager.toggleSelection({
      id: brokenLink.documentId,
    })
  }

  const allBrokenLinksSelected =
    selectionManager.selectedCount === brokenLinks.length
  const hasArchivedBrokenLinks = brokenLinks.some(
    (brokenLink) => brokenLink.versionStatus === "archived",
  )

  const tableTitle = hasArchivedBrokenLinks
    ? "Documents causing broken links"
    : `Select all documents below (${brokenLinks.length} ${pluralize(
        brokenLinks.length,
        "document",
      )})`
  const tableFooter = `${itemsToPublish.length} ${pluralize(
    itemsToPublish.length,
    "draft",
  )} will be published`
  const additionalInfo = hasArchivedBrokenLinks
    ? "You have links to archived documents that can't be published automatically, you may want to fix them manually first."
    : "Selecting the documents below will resolve broken links by publishing them along with the originally selected documents."

  return (
    <Dialog
      onOpenChange={(open) => {
        onOpenChange(open)
        selectionManager.clearSelection()
      }}
      open={open}
      size="small"
    >
      <DialogHeader
        icon="alert"
        title="This action will provoke broken links"
      />

      <DialogContent>
        <Box padding={16} flexDirection="column" gap={8}>
          <DialogDescription asChild>
            <Text>
              Some of the selected documents may result in broken links as they
              link to unpublished or archived documents.
              <Text variant="emphasized"> {additionalInfo}</Text>
            </Text>
          </DialogDescription>

          <Table
            // the column layout can shift based on if the broken links can be
            // selected through a checkbox or not
            columnLayout={hasArchivedBrokenLinks ? "12fr 4fr" : "1fr 12fr 3fr"}
          >
            <TableHead>
              <TableRow>
                {!hasArchivedBrokenLinks && (
                  <TableCell>
                    <Checkbox
                      onClick={onSelectAll}
                      checked={allBrokenLinksSelected}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Text>{tableTitle}</Text>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brokenLinks.map((brokenLink) => (
                <BrokenLink
                  key={brokenLink.documentId}
                  brokenLink={brokenLink}
                  isSelectable={!hasArchivedBrokenLinks}
                  selected={selectionManager.isSelected(brokenLink.documentId)}
                  onSelect={() => onSelect(brokenLink)}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions>
        <Text variant="small" sx={{ flexGrow: 1 }}>
          {tableFooter}
        </Text>
        <DialogCancelButton>Cancel</DialogCancelButton>
        <DialogActionButton onClick={onProceedWithBrokenLinks} color="purple">
          Publish
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

interface BrokenLinkProps {
  isSelectable: boolean
  onSelect: () => void
  selected: boolean
  brokenLink: BrokenLink
}

function BrokenLink(props: BrokenLinkProps) {
  const { brokenLink, isSelectable, onSelect, selected } = props
  return (
    <TableRow key={brokenLink.documentId} onClick={onSelect}>
      {isSelectable && (
        <TableCell>
          <Checkbox onClick={onSelect} checked={selected} />
        </TableCell>
      )}
      <TableCell>
        <Box flexDirection="column">
          <Text>
            {brokenLink.title} ({VersionStatusMap[brokenLink.versionStatus]})
          </Text>
          <Text color="grey11">{brokenLink.customTypeLabel}</Text>
        </Box>
      </TableCell>
      <TableCell align="end">
        <Link
          to={`/pages/${brokenLink.documentId}?s=${brokenLink.versionStatus}`}
          style={{ textDecoration: "none" }}
        >
          <Button color="grey">Go to page</Button>
        </Link>
      </TableCell>
    </TableRow>
  )
}

const VersionStatusMap: Record<string, "Archived" | "Published" | "Draft"> = {
  archived: "Archived",
  published: "Published",
  unclassified: "Draft",
}

function Fallback() {
  return <IconButton icon="upload" disabled color="grey" />
}
